import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useParams } from 'react-router-dom';
import { db, auth, googleProvider } from './firebase';
import { signInWithPopup, onAuthStateChanged, signOut } from 'firebase/auth';
import {
  collection, addDoc, query, onSnapshot, doc, updateDoc,
  deleteDoc, getDoc, orderBy, increment, setDoc, getDocs
} from 'firebase/firestore';
import {
  MapPin, CheckCircle, Share2, Smartphone, Image as ImageIcon,
  Trash2, Plus, Music, Eye, Edit2, X, Check, Clock
} from 'lucide-react';

const ADMIN_EMAIL = "gokulgod007@gmail.com";

// ─── EVENT CATEGORIES ────────────────────────────────────────────────────────
const EVENT_CATEGORIES = [
  {
    category: "Wedding Pre-Events", emoji: "💍",
    events: [
      { id: "save_the_date", label: "Save the Date" },
      { id: "roka", label: "Roka Ceremony" },
      { id: "engagement", label: "Engagement / Sagai" },
      { id: "haldi", label: "Haldi Ceremony" },
      { id: "mehendi", label: "Mehendi / Mehndi" },
      { id: "sangeet", label: "Sangeet Night" },
      { id: "garba", label: "Raas-Garba" },
    ],
  },
  {
    category: "Wedding Ceremony", emoji: "🎊",
    events: [
      { id: "hindu_wedding", label: "Hindu Wedding" },
      { id: "muslim_nikah", label: "Muslim / Nikah" },
      { id: "sikh_wedding", label: "Sikh / Anand Karaj" },
      { id: "christian_wedding", label: "Christian Wedding" },
      { id: "south_indian_wedding", label: "South Indian Wedding" },
      { id: "bengali_wedding", label: "Bengali Wedding" },
      { id: "marathi_wedding", label: "Marathi Wedding" },
      { id: "gujarati_wedding", label: "Gujarati Wedding" },
      { id: "punjabi_wedding", label: "Punjabi Wedding" },
      { id: "interfaith_wedding", label: "Interfaith Wedding" },
    ],
  },
  {
    category: "Post-Wedding", emoji: "🥂",
    events: [
      { id: "reception", label: "Reception" },
      { id: "griha_pravesh", label: "Griha Pravesh / Housewarming" },
      { id: "anniversary", label: "Wedding Anniversary" },
    ],
  },
  {
    category: "Life Milestones", emoji: "👶",
    events: [
      { id: "baby_shower", label: "Baby Shower / Godh Bharai" },
      { id: "namkaran", label: "Naming Ceremony / Namkaran" },
      { id: "birthday_first", label: "First Birthday" },
      { id: "birthday", label: "Birthday Party" },
      { id: "cradle", label: "Cradle Ceremony" },
    ],
  },
  {
    category: "Religious / Puja", emoji: "🙏",
    events: [
      { id: "satyanarayan", label: "Satyanarayan Puja" },
      { id: "grihapravesh_puja", label: "Grihapravesh Puja" },
      { id: "upanayana", label: "Thread Ceremony / Upanayana" },
      { id: "mundan", label: "Mundan Ceremony" },
      { id: "navratri", label: "Navratri / Diwali Party" },
    ],
  },
  {
    category: "Social Events", emoji: "🎓",
    events: [
      { id: "graduation", label: "Graduation Party" },
      { id: "farewell", label: "Farewell" },
      { id: "corporate", label: "Corporate / Office Event" },
      { id: "reunion", label: "Family / School Reunion" },
      { id: "kitty_party", label: "Kitty Party" },
    ],
  },
];

const EVENT_NAME_CONFIG = {
  save_the_date:        { type: "couple",   label1: "Partner 1 Name",           label2: "Partner 2 Name",   placeholder: "Rahul & Priya" },
  roka:                 { type: "couple",   label1: "Partner 1 Name",           label2: "Partner 2 Name",   placeholder: "Rahul & Priya" },
  engagement:           { type: "couple",   label1: "Partner 1 Name",           label2: "Partner 2 Name",   placeholder: "Rahul & Priya" },
  haldi:                { type: "couple",   label1: "Partner 1 Name",           label2: "Partner 2 Name",   placeholder: "Rahul & Priya" },
  mehendi:              { type: "couple",   label1: "Bride Name",               label2: "Partner Name",     placeholder: "Priya & Rahul" },
  sangeet:              { type: "couple",   label1: "Partner 1 Name",           label2: "Partner 2 Name",   placeholder: "Rahul & Priya" },
  garba:                { type: "host",     label1: "Host / Family Name",                                   placeholder: "The Sharma Family" },
  hindu_wedding:        { type: "couple",   label1: "Groom Name",               label2: "Bride Name",       placeholder: "Rahul & Priya" },
  muslim_nikah:         { type: "couple",   label1: "Groom Name",               label2: "Bride Name",       placeholder: "Ahmed & Fatima" },
  sikh_wedding:         { type: "couple",   label1: "Groom Name",               label2: "Bride Name",       placeholder: "Gurpreet & Simran" },
  christian_wedding:    { type: "couple",   label1: "Groom Name",               label2: "Bride Name",       placeholder: "David & Mary" },
  south_indian_wedding: { type: "couple",   label1: "Groom Name",               label2: "Bride Name",       placeholder: "Karthik & Deepa" },
  bengali_wedding:      { type: "couple",   label1: "Groom Name",               label2: "Bride Name",       placeholder: "Arjun & Puja" },
  marathi_wedding:      { type: "couple",   label1: "Groom Name",               label2: "Bride Name",       placeholder: "Rohan & Sneha" },
  gujarati_wedding:     { type: "couple",   label1: "Groom Name",               label2: "Bride Name",       placeholder: "Jay & Meera" },
  punjabi_wedding:      { type: "couple",   label1: "Groom Name",               label2: "Bride Name",       placeholder: "Harpreet & Manpreet" },
  interfaith_wedding:   { type: "couple",   label1: "Partner 1 Name",           label2: "Partner 2 Name",   placeholder: "Aryan & Sophia" },
  reception:            { type: "couple",   label1: "Partner 1 Name",           label2: "Partner 2 Name",   placeholder: "Rahul & Priya" },
  griha_pravesh:        { type: "host",     label1: "Host / Family Name",                                   placeholder: "The Sharma Family" },
  anniversary:          { type: "couple",   label1: "Partner 1 Name",           label2: "Partner 2 Name",   placeholder: "Rahul & Priya" },
  baby_shower:          { type: "honoree",  label1: "Mother-to-be Name",                                    placeholder: "Ananya" },
  namkaran:             { type: "honoree",  label1: "Baby's Name",                                          placeholder: "Baby Aryan" },
  birthday_first:       { type: "honoree",  label1: "Child's Name",                                        placeholder: "Little Arya" },
  birthday:             { type: "honoree",  label1: "Celebrant Name",                                      placeholder: "Rohan" },
  cradle:               { type: "honoree",  label1: "Baby's Name",                                          placeholder: "Baby Kavya" },
  satyanarayan:         { type: "host",     label1: "Host / Family Name",                                   placeholder: "The Patel Family" },
  grihapravesh_puja:    { type: "host",     label1: "Host / Family Name",                                   placeholder: "The Sharma Family" },
  upanayana:            { type: "honoree",  label1: "Child's Name",                                        placeholder: "Aarav" },
  mundan:               { type: "honoree",  label1: "Child's Name",                                        placeholder: "Baby Vivaan" },
  navratri:             { type: "host",     label1: "Organizer / Family Name",                              placeholder: "The Gupta Family" },
  graduation:           { type: "honoree",  label1: "Graduate's Name",                                     placeholder: "Priya Sharma" },
  farewell:             { type: "honoree",  label1: "Person Being Farewelled",                             placeholder: "Rahul Kumar" },
  corporate:            { type: "host",     label1: "Organization Name",                                    placeholder: "TechCorp Pvt Ltd" },
  reunion:              { type: "host",     label1: "Group / Family Name",                                  placeholder: "Class of 2010" },
  kitty_party:          { type: "host",     label1: "Host Name",                                            placeholder: "Mrs. Sunita Sharma" },
};

// ─── BLESSINGS per event type ─────────────────────────────────────────────────
const EVENT_BLESSINGS = {
  hindu_wedding:        "🙏 शुभ विवाह — ॐ सर्वे भवन्तु सुखिनः",
  muslim_nikah:         "☪️ Mabrook — بِسْمِ اللهِ الرَّحْمٰنِ الرَّحِيْمِ",
  sikh_wedding:         "✨ Waheguru Ji Ka Khalsa, Waheguru Ji Ki Fateh",
  christian_wedding:    "✝️ What God has joined together, let no one separate",
  south_indian_wedding: "🌺 திருமணம் வாழ்த்துக்கள் — Subha Muhurtham",
  bengali_wedding:      "🌸 শুভ বিবাহ — আশীর্বাদ করি",
  marathi_wedding:      "🌼 शुभ विवाह — देव तुम्हाला सुखी ठेवो",
  gujarati_wedding:     "💛 શુભ લગ્ન — ભગવાન તમને સુખી રાખે",
  punjabi_wedding:      "🎊 ਵਾਹਿਗੁਰੂ ਦੀ ਕਿਰਪਾ — ਸ਼ੁਭ ਵਿਆਹ",
  haldi:                "💛 हल्दी की रस्म — हर्षोल्लास के साथ",
  mehendi:              "🌿 मेहंदी की खुशबू — शुभकामनाएं",
  sangeet:              "🎵 संगीत की रात — मंगलमय हो",
  baby_shower:          "👶 गोद भराई — नन्हे मेहमान का स्वागत",
  namkaran:             "🌟 नामकरण संस्कार — शुभ नाम, शुभ जीवन",
  satyanarayan:         "🙏 ॐ नमो भगवते वासुदेवाय — सत्यनारायण कथा",
  upanayana:            "🔱 ॐ ब्रह्मचर्यमागच्छ — उपनयन संस्कार",
};

// ─── MESSAGE TEMPLATES per event type ────────────────────────────────────────
const MESSAGE_TEMPLATES = {
  hindu_wedding:    ["With the blessings of our elders and the grace of the Almighty, we joyfully invite you to celebrate our wedding.", "Two hearts, one soul. We invite you to be a part of our special day as we begin this beautiful journey together."],
  muslim_nikah:     ["By the grace of Allah, we are pleased to invite you to witness our Nikah ceremony.", "In the name of Allah, the Most Gracious, we request your presence and prayers on our wedding day."],
  sikh_wedding:     ["With Waheguru's blessings, we cordially invite you to the Anand Karaj ceremony.", "As we embark on this sacred journey together, we seek your blessings and presence."],
  christian_wedding:["By the grace of God, we joyfully invite you to witness the celebration of our marriage.", "Two lives, one love, in the presence of God. Please join us on our special day."],
  birthday:         ["Join us as we celebrate another wonderful year! Your presence will make this day even more special.", "Life is short and birthdays are sweet — come celebrate with us!"],
  baby_shower:      ["A little one is on the way! Join us as we shower the mother-to-be with love and blessings.", "New life, new love, new beginnings — please join us to celebrate!"],
  graduation:       ["Hard work, dedication, and countless cups of chai later — we made it! Join us to celebrate this milestone.", "The tassel was worth the hassle! Please join us to celebrate this achievement."],
  default:          ["With great joy, we request your gracious presence at our celebration.", "Your presence will make this occasion truly memorable. We look forward to celebrating with you."],
};


// ─── THEMES ──────────────────────────────────────────────────────────────────
const THEMES = [
  { id: 'royal',    name: 'Royal Gold',     bg: '#0a0a0a', text: '#D4AF37', accent: '#D4AF37', border: 'border-gold/30',        preview: ['#0a0a0a','#D4AF37','#1a1500'] },
  { id: 'water',    name: 'Ocean Water',    bg: '#051622', text: '#deeff5', accent: '#00b4d8', border: 'border-blue-400/30',    preview: ['#051622','#00b4d8','#0a2a3a'] },
  { id: 'forest',   name: 'Deep Forest',    bg: '#0a1a0f', text: '#e0f2e9', accent: '#4ade80', border: 'border-green-400/30',   preview: ['#0a1a0f','#4ade80','#122010'] },
  { id: 'formal',   name: 'Full Formal',    bg: '#ffffff', text: '#1a1a1a', accent: '#000000', border: 'border-gray-300',       preview: ['#ffffff','#000000','#f0f0f0'] },
  { id: 'midnight', name: 'Midnight',       bg: '#020617', text: '#f8fafc', accent: '#6366f1', border: 'border-indigo-400/30',  preview: ['#020617','#6366f1','#0a0e28'] },
  { id: 'rose',     name: 'Vintage Rose',   bg: '#1a0a0e', text: '#fce7f3', accent: '#fb7185', border: 'border-rose-400/30',    preview: ['#1a0a0e','#fb7185','#2a1018'] },
  { id: 'sunset',   name: 'Desert Sunset',  bg: '#2d1a0a', text: '#ffedd5', accent: '#f97316', border: 'border-orange-400/30',  preview: ['#2d1a0a','#f97316','#3a2010'] },
  { id: 'lavender', name: 'Lavender',       bg: '#120a1a', text: '#f5f3ff', accent: '#a78bfa', border: 'border-purple-400/30',  preview: ['#120a1a','#a78bfa','#1e1028'] },
  { id: 'emerald',  name: 'Emerald Silk',   bg: '#062c23', text: '#ecfdf5', accent: '#10b981', border: 'border-emerald-400/30', preview: ['#062c23','#10b981','#083a2f'] },
  { id: 'minimal',  name: 'Noir Minimal',   bg: '#000000', text: '#ffffff', accent: '#ffffff', border: 'border-white/20',       preview: ['#000000','#ffffff','#111111'] },
];

// ─── EFFECTS ─────────────────────────────────────────────────────────────────
const EFFECTS = [
  { id: 'none',      name: '🚫 None' },
  { id: 'snow',      name: '❄️ Snowfall' },
  { id: 'rain',      name: '🌧️ Rain' },
  { id: 'wind',      name: '🍃 Leaves' },
  { id: 'petals',    name: '🌸 Petals' },
  { id: 'gold-dust', name: '✨ Gold Dust' },
  { id: 'stars',     name: '⭐ Night Stars' },
  { id: 'bubbles',   name: '🫧 Pearl Bubbles' },
  { id: 'hearts',    name: '❤️ Floating Love' },
  { id: 'confetti',  name: '🎉 Celebration' },
  { id: 'fireflies', name: '💡 Fireflies' },
  { id: 'diamonds',  name: '💎 Royal Diamonds' },
  { id: 'autumn',    name: '🍂 Autumn Gold' },
  { id: 'blossom',   name: '🌸 Cherry Blossom' },
  { id: 'glimmer',   name: '💫 Magic Glimmer' },
];


// ─── LANGUAGES ────────────────────────────────────────────────────────────────
const LANGUAGES = [
  { id: 'en',    label: '🇬🇧 English' },
  { id: 'hi',    label: '🇮🇳 हिन्दी' },
  { id: 'ta',    label: '🇮🇳 தமிழ்' },
  { id: 'te',    label: '🇮🇳 తెలుగు' },
  { id: 'kn',    label: '🇮🇳 ಕನ್ನಡ' },
  { id: 'mr',    label: '🇮🇳 मराठी' },
  { id: 'gu',    label: '🇮🇳 ગુજરાતી' },
  { id: 'bn',    label: '🇮🇳 বাংলা' },
  { id: 'pa',    label: '🇮🇳 ਪੰਜਾਬੀ' },
]; 

// ─── LANGUAGE LABELS ──────────────────────────────────────────────────────────
const LANG_LABELS = {
  en: {
    saveDate: "Save the Date",
    subEvents: "Event Schedule",
    openVenue: "Open Venue",
  },
  hi: {
    saveDate: "तारीख याद रखें",
    subEvents: "कार्यक्रम की अनुसूची",
    openVenue: "स्थान देखें",
  },
  ta: {
    saveDate: "தேதியை சேமிக்கவும்",
    subEvents: "நிகழ்ச்சி நிரல்",
    openVenue: "இடத்தைத் திறக்கவும்",
  },
  te: {
    saveDate: "తేదీని గుర్తుంచుకోండి",
    subEvents: "కార్యక్రమ వివరాలు",
    openVenue: "వేదికను చూడండి",
  },
  kn: {
    saveDate: "ದಿನಾಂಕ ನೆನಪಿಡಿ",
    subEvents: "ಕಾರ್ಯಕ್ರಮದ ವೇಳಾಪಟ್ಟಿ",
    openVenue: "ಸ್ಥಳವನ್ನು ನೋಡಿ",
  },
  mr: {
    saveDate: "तारीख लक्षात ठेवा",
    subEvents: "कार्यक्रम वेळापत्रक",
    openVenue: "ठिकाण पहा",
  },
  gu: {
    saveDate: "તારીખ સાચવો",
    subEvents: "કાર્યક્રમ શેડ્યૂલ",
    openVenue: "સ્થળ જુઓ",
  },
  bn: {
    saveDate: "তারিখটি মনে রাখুন",
    subEvents: "অনুসূচী",
    openVenue: "ভেন্যু দেখুন",
  },
  pa: {
    saveDate: "ਤਰੀਕ ਯਾਦ ਰੱਖੋ",
    subEvents: "ਪ੍ਰੋਗਰਾਮ ਦੀ ਸੂਚੀ",
    openVenue: "ਸਥਾਨ ਦੇਖੋ",
  }
};

// ─── IMAGE COMPRESSION ────────────────────────────────────────────────────────
const compressImage = (base64Str, maxWidth = 1200, maxHeight = 1200) =>
  new Promise((resolve) => {
    const img = new Image();
    img.src = base64Str;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let w = img.width, h = img.height;
      if (w > h) { if (w > maxWidth) { h *= maxWidth / w; w = maxWidth; } }
      else { if (h > maxHeight) { w *= maxHeight / h; h = maxHeight; } }
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL('image/jpeg', 0.7));
    };
  });

// ─── AMBIENCE ─────────────────────────────────────────────────────────────────
const Ambience = ({ type, accentColor }) => {
  if (!type || type === 'none') return null;
  const elements = Array.from({ length: 25 });
  const getStyle = (i) => {
    const isFloating = ['fireflies','stars','glimmer','bubbles'].includes(type);
    const delay    = Math.random() * 5;
    const duration = Math.random() * 4 + (type === 'rain' ? 1.5 : 4);
    const size     = type === 'gold-dust' ? '3px' : type === 'bubbles' ? '12px' : '8px';
    let color      = accentColor;
    if (['gold-dust','diamonds'].includes(type)) color = '#D4AF37';
    if (['stars','snow'].includes(type))          color = '#ffffff';
    if (type === 'hearts')                         color = '#ff4d6d';
    if (['blossom','petals'].includes(type))       color = '#ffb7c5';
    if (type === 'autumn')                         color = '#d97706';
    return {
      width: type === 'rain' ? '1px' : size,
      height: type === 'rain' ? '20px' : size,
      left: Math.random() * 100 + '%',
      top: isFloating ? Math.random() * 100 + '%' : '-10%',
      backgroundColor: type === 'bubbles' ? 'transparent' : color,
      border: type === 'bubbles' ? `1px solid ${color}` : 'none',
      boxShadow: ['gold-dust','stars','fireflies','glimmer'].includes(type) ? `0 0 8px ${color}` : 'none',
      animationDuration: `${duration}s`,
      animationDelay: `${delay}s`,
      opacity: Math.random() * 0.4 + 0.3,
      borderRadius: type === 'diamonds' ? '0' : '50%',
      transform: type === 'diamonds' ? 'rotate(45deg)' : 'none',
    };
  };
  return (
<div className="fixed inset-0 pointer-events-none overflow-hidden z-[100]"> 
      {/* Changed 'absolute' to 'fixed' to cover the whole screen during scroll */}
      {/* Changed z-index to 100 to stay above all text and images */}
      {elements.map((_, i) => (
<div key={i}
          className={`absolute ${['fireflies','stars','glimmer','bubbles'].includes(type) ? 'animate-float' : type === 'rain' ? 'animate-fall-straight' : 'animate-fall-sway'}`}
          style={getStyle(i)}
        />
      ))}
      <style>{`
        @keyframes fallSway { 0%{transform:translateY(-10vh) translateX(0) rotate(0deg);opacity:0} 20%{opacity:.6} 100%{transform:translateY(110vh) translateX(50px) rotate(360deg);opacity:0} }
        @keyframes fallStraight { 0%{transform:translateY(-10vh);opacity:0} 20%{opacity:.4} 100%{transform:translateY(110vh);opacity:0} }
        @keyframes float { 0%,100%{transform:translate(0,0);opacity:.3} 50%{transform:translate(30px,-30px);opacity:.8} }
        .animate-fall-sway{animation:fallSway linear infinite}
        .animate-fall-straight{animation:fallStraight linear infinite}
        .animate-float{animation:float ease-in-out infinite}
      `}</style>
    </div>
  );
};

// ─── COUNTDOWN TIMER ──────────────────────────────────────────────────────────
const Countdown = ({ date, accentColor, borderClass }) => {
  const [timeLeft, setTimeLeft] = useState({});
  useEffect(() => {
    const calc = () => {
      const diff = new Date(date) - new Date();
      if (diff <= 0) return setTimeLeft({ done: true });
      setTimeLeft({
        days:    Math.floor(diff / 86400000),
        hours:   Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
      });
    };
    calc();
    const t = setInterval(calc, 1000);
    return () => clearInterval(t);
  }, [date]);

  if (timeLeft.done) return <p className="text-center opacity-60 font-cinzel text-sm uppercase tracking-widest">The celebration is here! 🎉</p>;
  if (!timeLeft.days && timeLeft.days !== 0) return null;

  return (
    <div className={`py-8 border-y ${borderClass} text-center`}>
      <p className="text-[10px] uppercase tracking-widest opacity-40 mb-4 font-cinzel">Counting Down</p>
      <div className="flex justify-center gap-4 md:gap-8">
        {[['days', timeLeft.days], ['hours', timeLeft.hours], ['mins', timeLeft.minutes], ['secs', timeLeft.seconds]].map(([label, val]) => (
          <div key={label} className="flex flex-col items-center">
            <span className="text-3xl md:text-5xl font-cinzel font-bold" style={{ color: accentColor }}>{String(val).padStart(2,'0')}</span>
            <span className="text-[9px] uppercase tracking-widest opacity-40 mt-1">{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── THEME SWATCH PICKER ──────────────────────────────────────────────────────
const ThemePicker = ({ value, onChange }) => (
  <div>
    <p className="text-[10px] text-white/40 uppercase tracking-widest mb-2">Theme</p>
    <div className="flex flex-wrap gap-2">
      {THEMES.map(t => (
        <button
          key={t.id}
          type="button"
          title={t.name}
          onClick={() => onChange(t.id)}
          className={`relative w-8 h-8 rounded-full border-2 transition-all overflow-hidden ${value === t.id ? 'border-gold scale-110' : 'border-transparent opacity-60 hover:opacity-100'}`}
          style={{ background: `linear-gradient(135deg, ${t.preview[0]} 50%, ${t.preview[1]} 50%)` }}
        >
          {value === t.id && (
            <span className="absolute inset-0 flex items-center justify-center text-[8px]">✓</span>
          )}
        </button>
      ))}
    </div>
    <p className="text-[10px] text-gold/40 mt-1">{THEMES.find(t => t.id === value)?.name}</p>
  </div>
);

// ─── SUB-EVENT MANAGER ────────────────────────────────────────────────────────
const SubEventManager = ({ subEvents, onChange }) => {
  const add = () => onChange([...subEvents, { name: '', date: '', time: '', venue: '' }]);
  const remove = (i) => onChange(subEvents.filter((_, idx) => idx !== i));
  const update = (i, field, val) => {
    const updated = [...subEvents];
    updated[i] = { ...updated[i], [field]: val };
    onChange(updated);
  };
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-[10px] text-white/40 uppercase tracking-widest">Sub-Events / Schedule</p>
        <button type="button" onClick={add} className="flex items-center gap-1 text-gold text-[10px] border border-gold/30 px-2 py-1 rounded-full hover:bg-gold/5">
          <Plus size={10}/> Add Event
        </button>
      </div>
      {subEvents.map((ev, i) => (
        <div key={i} className="bg-black/40 rounded-xl p-3 border border-white/5 space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-gold/60 text-[10px] uppercase">Event {i+1}</span>
            <button type="button" onClick={() => remove(i)} className="text-red-500/50 hover:text-red-500"><X size={12}/></button>
          </div>
          <input className="w-full bg-transparent border-b border-white/10 text-white text-xs p-1 outline-none focus:border-gold" placeholder="Event name (e.g. Haldi)" value={ev.name} onChange={e => update(i,'name',e.target.value)} />
          <div className="grid grid-cols-2 gap-2">
            <input type="date" className="bg-black/60 p-2 rounded-lg text-xs text-white border border-white/5 outline-none" value={ev.date} onChange={e => update(i,'date',e.target.value)} />
            <input type="time" className="bg-black/60 p-2 rounded-lg text-xs text-white border border-white/5 outline-none" value={ev.time} onChange={e => update(i,'time',e.target.value)} />
          </div>
          <input className="w-full bg-transparent border-b border-white/10 text-white text-xs p-1 outline-none focus:border-gold" placeholder="Venue name" value={ev.venue} onChange={e => update(i,'venue',e.target.value)} />
        </div>
      ))}
    </div>
  );
};

// ─── EVENT PICKER ─────────────────────────────────────────────────────────────
function EventPicker({ onSelect }) {
  const [openCategory, setOpenCategory] = useState(null);
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col items-center justify-start p-6 md:p-12">
      <h2 className="text-3xl font-cinzel text-gold mb-2 uppercase text-center tracking-widest mt-8">Create Invitation</h2>
      <p className="text-white/30 text-xs uppercase tracking-widest mb-8">Choose your event type</p>
      <div className="w-full max-w-2xl space-y-4">
    {EVENT_CATEGORIES.map((cat, index) => {
      const isOpen = openCategory === cat.category;
      
      return (
        <div 
          key={cat.category} 
          className={`rounded-2xl overflow-hidden border transition-all duration-500 ${
            isOpen ? 'border-gold/40 shadow-[0_0_20px_rgba(212,175,55,0.1)]' : 'border-white/5'
          }`}
          style={{ animationDelay: `${index * 100}ms` }}
        >
          <button
            className={`w-full flex justify-between items-center px-6 py-5 bg-[#111] text-sm font-cinzel tracking-widest transition-all duration-300 ${
              isOpen ? 'text-gold' : 'text-white/60 hover:text-white hover:bg-[#161616]'
            }`}
            onClick={() => setOpenCategory(isOpen ? null : cat.category)}
          >
            <div className="flex items-center gap-4">
              <span className={`text-xl transition-transform duration-300 ${isOpen ? 'scale-125' : 'grayscale'}`}>
                {cat.emoji}
              </span>
              <span className={isOpen ? 'font-bold' : ''}>{cat.category}</span>
            </div>
            <span className={`text-gold transition-transform duration-300 ${isOpen ? 'rotate-180' : 'rotate-0'}`}>
              ▼
            </span>
          </button>

          {/* Animated Content Area */}
          <div 
            className={`transition-all duration-500 ease-in-out overflow-hidden ${
              isOpen ? 'max-h-[500px] opacity-100 border-t border-gold/10' : 'max-h-0 opacity-0'
            }`}
          >
            <div className="bg-black/40 p-6 flex flex-wrap gap-3">
              {cat.events.map((ev) => (
                <button 
                  key={ev.id} 
                  onClick={() => onSelect(ev)} 
                  className="group relative px-5 py-2.5 rounded-full border border-gold/20 bg-[#0f0f0f] text-white/70 text-[11px] uppercase tracking-wider hover:border-gold hover:text-gold hover:shadow-[0_0_10px_rgba(212,175,55,0.2)] transition-all duration-300"
                >
                  <span className="relative z-10">{ev.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      );
    })}
  </div>
    </div>
  );
}

// ─── WEDDING VIEW (Public Page) ───────────────────────────────────────────────
function WeddingView() {
  const { id } = useParams();
  const [data,       setData]       = useState(null);
  //const [rsvpStatus, setRsvpStatus] = useState(null); // 'yes'|'no'|null
  const [rsvpCounts, setRsvpCounts] = useState({ yes: 0, no: 0 });
  const [musicPlaying, setMusicPlaying] = useState(false);
  const audioRef = useRef(null);

  // Load invite data + increment view count
  useEffect(() => {
    const load = async () => {
      const ref  = doc(db, "submissions", id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return;
      setData(snap.data());
      // Increment view count
      await updateDoc(ref, { viewCount: increment(1) });
    };
    load();
  }, [id]);

  // Live RSVP counts
  useEffect(() => {
    if (!id) return;
    const q = collection(db, "submissions", id, "rsvps");
    return onSnapshot(q, (snap) => {
      let yes = 0, no = 0;
      snap.docs.forEach(d => { if (d.data().response === 'yes') yes++; else no++; });
      setRsvpCounts({ yes, no });
    });
  }, [id]);

  // Check if current device already RSVPd
  useEffect(() => {
 //   const saved = localStorage.getItem(`rsvp_${id}`);
  //  if (saved) setRsvpStatus(saved);
  }, [id]);

  const handleRSVP = async (response) => {
    const deviceId = localStorage.getItem('deviceId') || (() => {
      const id2 = Math.random().toString(36).slice(2);
      localStorage.setItem('deviceId', id2);
      return id2;
    })();
    await setDoc(doc(db, "submissions", id, "rsvps", deviceId), { response, ts: new Date() });
 //   setRsvpStatus(response);
 //   localStorage.setItem(`rsvp_${id}`, response);
  };

  const shareOnWhatsApp = () => {
    const url  = window.location.href;
    const text = `You're invited! ${data?.name ? `— ${data.name}` : ''} 🎉\n${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const toggleMusic = () => {
    if (!audioRef.current) return;
    if (musicPlaying) { audioRef.current.pause(); setMusicPlaying(false); }
    else              { audioRef.current.play();  setMusicPlaying(true);  }
  };

  if (!data) return <div className="h-screen bg-black flex items-center justify-center text-gold font-cinzel text-sm tracking-widest">LOADING...</div>;

  const activeTheme = THEMES.find(t => t.id === data.themeId) || THEMES[0];
  const lang        = data.language || 'en';
  const L           = LANG_LABELS[lang] || LANG_LABELS.en;
  const blessing    = EVENT_BLESSINGS[data.eventId];

  return (
    <div className="min-h-screen relative overflow-x-hidden" style={{ backgroundColor: activeTheme.bg, color: activeTheme.text }}>
      {/* Background music (hidden) */}
      {data.musicUrl && (
        <audio ref={audioRef} src={data.musicUrl} loop preload="none" />
      )}

      <Ambience type={data.effect} accentColor={activeTheme.accent} />

      {/* Music toggle FAB */}
      {data.musicUrl && (
        <button onClick={toggleMusic}
          className="fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full flex items-center justify-center shadow-2xl border"
          style={{ backgroundColor: activeTheme.bg, borderColor: activeTheme.accent, color: activeTheme.accent }}>
          <Music size={18} className={musicPlaying ? 'animate-spin' : ''} style={{ animationDuration: '3s' }} />
        </button>
      )}

      {/* Cover */}
      <div className="flex flex-col items-center">
        <div className="w-full h-[60vh] md:h-[80vh] overflow-hidden">
          <img src={data.coverPhoto} className="w-full h-full object-cover" alt="Cover" />
        </div>
        <div className="py-12 px-4">
          {blessing && <p className="text-center text-xs opacity-50 mb-2 font-cinzel">{blessing}</p>}
          <h1 className="text-6xl md:text-9xl font-vibes text-center" style={{ color: activeTheme.accent }}>{data.name}</h1>
          {data.eventLabel && <p className="text-center text-xs uppercase tracking-widest mt-2 opacity-50">{data.eventLabel}</p>}
        </div>
      </div>

      <div className="max-w-4xl mx-auto py-10 px-6 text-center space-y-12">

        {/* Save the date */}
        <div className={`py-12 border-y ${activeTheme.border}`}>
          <h2 className="text-2xl font-cinzel mb-4 uppercase tracking-widest">{L.saveDate}</h2>
          <p className="text-xl font-light">{data.date}</p>
        </div>

        {/* Countdown */}
        {data.date && <Countdown date={data.date} accentColor={activeTheme.accent} borderClass={activeTheme.border} />}

        {/* Sub-events schedule */}
        {data.subEvents?.length > 0 && (
          <div className={`py-8 border-y ${activeTheme.border}`}>
            <h2 className="text-lg font-cinzel uppercase tracking-widest mb-6 opacity-70">{L.subEvents}</h2>
            <div className="space-y-3">
              {data.subEvents.map((ev, i) => (
                <div key={i} className="flex items-center justify-between py-3 px-6 rounded-xl" style={{ backgroundColor: activeTheme.accent + '15' }}>
                  <span className="font-cinzel text-sm" style={{ color: activeTheme.accent }}>{ev.name}</span>
                  <span className="text-xs opacity-60">{ev.date}{ev.time ? ` · ${ev.time}` : ''}</span>
                  {ev.venue && <span className="text-xs opacity-40 hidden md:block">{ev.venue}</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Venue */}
        <div className="flex flex-col items-center gap-4">
          <a href={data.location} target="_blank" rel="noreferrer"
            className="inline-block border-2 px-12 py-4 rounded-full font-bold uppercase tracking-widest transition-all hover:bg-white hover:text-black"
            style={{ borderColor: activeTheme.accent, color: activeTheme.accent }}>
            {L.openVenue}
          </a>
          <p className="opacity-60 italic font-cinzel text-sm uppercase">{data.hallName}</p>

          {/* Embedded Google Maps */}
          {data.mapEmbed && (
            <div className="w-full mt-4 rounded-2xl overflow-hidden h-64">
              <iframe
                title="venue-map"
                src={`https://maps.google.com/maps?q=${encodeURIComponent(data.hallName)}&output=embed`}
                width="100%" height="100%" style={{ border: 0 }} allowFullScreen loading="lazy"
              />
            </div>
          )}
        </div>

        {/* Message */}
        {data.message && (
          <p className="italic text-lg font-light leading-relaxed max-w-2xl mx-auto break-words px-4">"{data.message}"</p>
        )}

        {/* Gallery */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 pb-10">
          {data.galleryPhotos?.map((url, i) => (
            <img key={i} src={url} className="rounded-2xl w-full aspect-square object-cover shadow-2xl" alt="Gallery" />
          ))}
        </div>

        {/* WhatsApp Share */}
        <div className="pb-20">
          <button onClick={shareOnWhatsApp}
            className="inline-flex items-center gap-3 px-8 py-4 rounded-full font-bold text-sm uppercase tracking-widest transition-all hover:scale-105"
            style={{ backgroundColor: '#25D366', color: '#fff' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            Share on WhatsApp
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── GUEST REGISTRY ───────────────────────────────────────────────────────────
function GuestRegistry({ onComplete }) {
  const [step, setStep] = useState(1);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [formData, setFormData] = useState({
    name: '', name2: '', date: '', email: '', message: '',
    themeId: 'royal', effect: 'none', hallName: '', location: '',
    dressCode: '', language: 'en', musicUrl: '', mapEmbed: false,
    subEvents: [],
  });
  const [previews, setPreviews] = useState({ cover: null, gallery: [] });
  const [status, setStatus] = useState('idle');
  const [showTemplates, setShowTemplates] = useState(false);
  const [isPremiumUnlocked] = useState(false); // set true after payment in real app

  const activeTheme = THEMES.find(t => t.id === formData.themeId);
  const nameConfig  = selectedEvent ? EVENT_NAME_CONFIG[selectedEvent.id] : null;
  const displayName = nameConfig?.type === 'couple'
    ? [formData.name, formData.name2].filter(Boolean).join(' & ')
    : formData.name;

  const templates = MESSAGE_TEMPLATES[selectedEvent?.id] || MESSAGE_TEMPLATES.default;

  const handleEventSelect = (ev) => {
    setSelectedEvent(ev);
    setFormData(f => ({ ...f, name: '', name2: '' }));
    setStep(2);
  };

  const handleFile = async (e, type) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    if (type === 'cover') {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        const c = await compressImage(ev.target.result);
        setPreviews(p => ({ ...p, cover: c }));
      };
      reader.readAsDataURL(files[0]);
    } else {
      const slots   = 4 - previews.gallery.length;
      if (!slots) return;
      const processed = await Promise.all(files.slice(0, slots).map(file =>
        new Promise(res => {
          const r = new FileReader();
          r.onload = async ev2 => res(await compressImage(ev2.target.result));
          r.readAsDataURL(file);
        })
      ));
      setPreviews(p => ({ ...p, gallery: [...p.gallery, ...processed] }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!previews.cover) return alert("Please upload a cover photo");
    setStatus('processing');
    try {
      // Generate a preview token for shareable link before approval
      const previewToken = Math.random().toString(36).slice(2, 10);
      await addDoc(collection(db, "submissions"), {
        ...formData,
        name: displayName,
        eventId:       selectedEvent?.id,
        eventLabel:    selectedEvent?.label,
        coverPhoto:    previews.cover,
        galleryPhotos: previews.gallery,
        approved:      false,
        viewCount:     0,
        previewToken,
        timestamp:     new Date(),
      });
      setStatus('done');
      setTimeout(onComplete, 2000);
    } catch {
      alert("Submission failed. Try smaller photos.");
      setStatus('idle');
    }
  };

  if (status === 'done') return (
    <div className="h-screen bg-black flex items-center justify-center text-gold font-cinzel">
      Successfully Submitted! 🎉
    </div>
  );

  if (step === 1) return <EventPicker onSelect={handleEventSelect} />;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white p-6 md:p-12 flex flex-col lg:flex-row gap-10 items-start justify-center">
      <style>{`
        input[type="date"]::-webkit-calendar-picker-indicator,
        input[type="time"]::-webkit-calendar-picker-indicator { filter: invert(1); cursor: pointer; opacity: 0.6; }
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        select option { background: #111; color: white; }
      `}</style>

      <div className="flex-1 bg-[#111] p-8 rounded-3xl border border-white/5 space-y-6 w-full max-w-2xl">

        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <button onClick={() => setStep(1)} className="text-gold/60 font-cinzel text-[10px] uppercase border border-gold/20 px-3 py-1.5 rounded-full hover:border-gold/50">← Change</button>
          <span className="text-[10px] font-cinzel uppercase tracking-widest text-white/40 border border-white/10 px-3 py-1.5 rounded-full">{selectedEvent?.label}</span>
        </div>

        <h2 className="text-3xl font-cinzel text-gold mb-4 uppercase text-center tracking-widest">Create Invitation</h2>

        <form onSubmit={handleSubmit} className="space-y-5 text-center">

          {/* Name fields */}
          {nameConfig?.type === 'couple' ? (
            <div className="flex items-center gap-2">
              <input className="flex-1 bg-transparent border-b border-gray-800 p-3 text-2xl font-vibes focus:border-gold outline-none text-gold text-center"
                placeholder={nameConfig.label1} value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })} required />
              <span className="text-gold text-2xl font-vibes">&</span>
              <input className="flex-1 bg-transparent border-b border-gray-800 p-3 text-2xl font-vibes focus:border-gold outline-none text-gold text-center"
                placeholder={nameConfig.label2} value={formData.name2}
                onChange={e => setFormData({ ...formData, name2: e.target.value })} required />
            </div>
          ) : (
            <input className="w-full bg-transparent border-b border-gray-800 p-3 text-3xl font-vibes focus:border-gold outline-none text-gold text-center"
              placeholder={nameConfig?.label1 || "Name"} value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })} required />
          )}

          {/* Theme swatch picker */}
          <ThemePicker value={formData.themeId} onChange={v => setFormData({ ...formData, themeId: v })} />
          
              {/* Effect picker - Now everything is unlocked */}
<div>
  <p className="text-[10px] text-white/40 uppercase tracking-widest mb-2 text-left">Ambient Effect</p>
  <div className="grid grid-cols-3 gap-2">
    {EFFECTS.map(ef => (
      <button 
        key={ef.id} 
        type="button"
        onClick={() => setFormData({ ...formData, effect: ef.id })}
        className={`px-3 py-2 rounded-lg text-[10px] border transition-all text-left 
          ${formData.effect === ef.id 
            ? 'border-gold text-gold bg-gold/5' 
            : 'border-white/5 text-white/50 hover:border-white/20'
          }`}
      >
        {ef.name}
      </button>
    ))}
  </div>
</div>

          {/* Language selector */}
          <div className="text-left">
            <p className="text-[10px] text-white/40 uppercase tracking-widest mb-2">Invite Language</p>
            <div className="flex flex-wrap gap-2">
              {LANGUAGES.map(lang => (
                <button key={lang.id} type="button"
                  onClick={() => setFormData({ ...formData, language: lang.id })}
                  className={`px-3 py-1.5 rounded-full text-[10px] border transition-all ${formData.language === lang.id ? 'border-gold text-gold bg-gold/5' : 'border-white/5 text-white/40 hover:border-white/20'}`}>
                  {lang.label}
                </button>
              ))}
            </div>
          </div>
 
          {/* Date, venue, location */}
          <div className="grid grid-cols-2 gap-4">
            <input type="date" className="bg-black p-4 rounded-xl border border-white/5 text-xs text-white outline-none"
              onChange={e => setFormData({ ...formData, date: e.target.value })} required />
            <input className="bg-black p-4 rounded-xl border border-white/5 text-xs outline-none"
              placeholder="Venue Name" onChange={e => setFormData({ ...formData, hallName: e.target.value })} required />
          </div>
          <input className="w-full bg-black p-4 rounded-xl border border-white/5 text-xs outline-none"
            placeholder="Google Maps URL" onChange={e => setFormData({ ...formData, location: e.target.value })} required />

          {/* Message with templates */}
          <div className="relative">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] text-white/40 uppercase tracking-widest">Message</span>
              <button type="button" onClick={() => setShowTemplates(!showTemplates)}
                className="text-[10px] text-gold/60 border border-gold/20 px-2 py-1 rounded-full hover:border-gold/50">
                💬 Templates
              </button>
            </div>
            {showTemplates && (
              <div className="mb-2 bg-black/80 rounded-xl border border-white/10 overflow-hidden">
                {templates.map((t, i) => (
                  <button key={i} type="button"
                    onClick={() => { setFormData({ ...formData, message: t }); setShowTemplates(false); }}
                    className="w-full text-left text-xs p-3 text-white/60 hover:text-white hover:bg-white/5 border-b border-white/5 last:border-0">
                    {t}
                  </button>
                ))}
              </div>
            )}
            <textarea className="w-full bg-black p-4 rounded-xl border border-white/5 text-xs min-h-[80px] resize-none outline-none"
              placeholder="Message (Limit 500 chars)" maxLength="500" value={formData.message}
              onChange={e => setFormData({ ...formData, message: e.target.value })} />
          </div>

          {/* Background music URL */}
          <div className="text-left">
            <p className="text-[10px] text-white/40 uppercase tracking-widest mb-1">Background Music URL <span className="text-white/20">(mp3 direct link)</span></p>
            <input className="w-full bg-black p-4 rounded-xl border border-white/5 text-xs outline-none"
              placeholder="https://example.com/song.mp3"
              value={formData.musicUrl}
              onChange={e => setFormData({ ...formData, musicUrl: e.target.value })} />
          </div>

          {/* Map embed toggle */}
          <div className="flex items-center gap-3 text-left">
            <button type="button" onClick={() => setFormData({ ...formData, mapEmbed: !formData.mapEmbed })}
              className={`w-10 h-5 rounded-full transition-colors relative ${formData.mapEmbed ? 'bg-gold' : 'bg-white/10'}`}>
              <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all ${formData.mapEmbed ? 'left-5' : 'left-0.5'}`} />
            </button>
            <span className="text-[10px] text-white/40 uppercase tracking-widest">Show embedded map on invite</span>
          </div>

          {/* Sub-events */}
          <SubEventManager subEvents={formData.subEvents} onChange={v => setFormData({ ...formData, subEvents: v })} />

          {/* Photos */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-black/40 border border-white/5 rounded-2xl flex flex-col items-center gap-2">
              <span className="text-[10px] text-gold/60 uppercase">1. Cover Photo</span>
              <input type="file" accept="image/*" onChange={e => handleFile(e, 'cover')} className="hidden" id="coverUpload" />
              <label htmlFor="coverUpload" className={`cursor-pointer px-4 py-2 rounded-lg text-[10px] font-bold border transition-all ${previews.cover ? 'border-green-500/50 text-green-500' : 'border-gold/30 text-gold hover:bg-gold/5'}`}>
                {previews.cover ? "CHANGE PHOTO ✓" : "CHOOSE COVER"}
              </label>
            </div>
            <div className="p-4 bg-black/40 border border-white/5 rounded-2xl flex flex-col items-center gap-2">
              <span className="text-[10px] text-gold/60 uppercase">2. Gallery ({previews.gallery.length}/4)</span>
              <input type="file" accept="image/*" multiple onChange={e => handleFile(e, 'gallery')} className="hidden" id="galUpload" disabled={previews.gallery.length >= 4} />
              <label htmlFor="galUpload" className={`px-4 py-2 rounded-lg text-[10px] font-bold border transition-all ${previews.gallery.length >= 4 ? 'opacity-20 cursor-not-allowed border-gray-500' : 'cursor-pointer border-gold/30 text-gold hover:bg-gold/5'}`}>
                {previews.gallery.length >= 4 ? "GALLERY FULL" : "SELECT UP TO 4"}
              </label>
            </div>
          </div>

          {previews.gallery.length > 0 && (
            <div className="flex flex-wrap gap-2 justify-center py-2 bg-white/5 rounded-xl">
              {previews.gallery.map((img, i) => (
                <div key={i} className="relative w-12 h-12">
                  <img src={img} className="w-full h-full object-cover rounded-md border border-white/10" alt="" />
                  <button type="button" onClick={() => setPreviews(p => ({ ...p, gallery: p.gallery.filter((_,idx) => idx !== i) }))}
                    className="absolute -top-1 -right-1 bg-red-500 rounded-full p-0.5"><Trash2 size={10} color="white"/></button>
                </div>
              ))}
            </div>
          )}

          <button type="submit" disabled={status === 'processing'}
            className="w-full bg-gold text-black py-4 rounded-xl font-bold uppercase tracking-widest shadow-lg disabled:opacity-50 hover:bg-yellow-500 transition-colors">
            {status === 'processing' ? 'COMPRESSING...' : 'Submit for Approval'}
          </button>
        </form>
      </div>

      {/* Mobile Preview */}
      <div className="flex-1 flex flex-col items-center sticky top-12">
        <div className="flex items-center gap-2 mb-4 text-gold/50 font-cinzel text-[10px] uppercase tracking-[0.3em]"><Smartphone size={14}/> Mobile Preview</div>
        <div className="relative w-[320px] h-[640px] border-[10px] border-[#1a1a1a] rounded-[3.5rem] overflow-hidden bg-black shadow-2xl">
          <div className="h-full overflow-y-auto hide-scrollbar flex flex-col" style={{ backgroundColor: activeTheme.bg, color: activeTheme.text }}>
            <Ambience type={formData.effect} accentColor={activeTheme.accent} />
            <div className="h-56 relative flex-shrink-0">
              {previews.cover
                ? <img src={previews.cover} className="w-full h-full object-cover" alt="Cover" />
                : <div className="w-full h-full bg-white/5 flex items-center justify-center"><ImageIcon className="opacity-10" size={40} /></div>}
            </div>
            <div className="pt-6 pb-1 text-center">
              <h1 className="text-3xl font-vibes px-4" style={{ color: activeTheme.accent }}>{displayName || "Name Preview"}</h1>
              {selectedEvent && <p className="text-[8px] uppercase tracking-widest opacity-30 mt-1">{selectedEvent.label}</p>}
            </div>
            <div className="p-6 text-center space-y-4 flex-grow">
              <div className={`py-4 border-y ${activeTheme.border}`}>
                <p className="text-[9px] uppercase opacity-60">Save The Date</p>
                <p className="text-xs font-cinzel">{formData.date || "—"}</p>
              </div>
              <div className="space-y-1">
                <div className="inline-block border px-6 py-2 rounded-full text-[9px] font-bold uppercase tracking-widest" style={{ borderColor: activeTheme.accent }}>Open Venue</div>
                <p className="text-[10px] opacity-70 uppercase block break-words mt-2 px-2">{formData.hallName || "Venue Name"}</p>
              </div>
              {formData.message && <p className="text-[11px] italic font-light leading-relaxed break-words opacity-90 px-2">"{formData.message}"</p>}
              <div className="grid grid-cols-2 gap-2 pb-6">
                {previews.gallery.map((src, i) => <img key={i} src={src} className="h-16 w-full object-cover rounded-lg" alt="G" />)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── EDIT MODAL ───────────────────────────────────────────────────────────────
function EditModal({ item, onClose }) {
  const [data, setData] = useState({ date: item.date, hallName: item.hallName, location: item.location, message: item.message });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    await updateDoc(doc(db, "submissions", item.id), data);
    setSaving(false);
    onClose();
  };
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-[#111] rounded-2xl border border-white/10 p-6 w-full max-w-md space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="font-cinzel text-gold text-sm uppercase tracking-widest">Edit Invitation</h3>
          <button onClick={onClose}><X size={16} className="text-white/40 hover:text-white" /></button>
        </div>
        <input type="date" className="w-full bg-black p-3 rounded-xl border border-white/5 text-xs text-white outline-none"
          value={data.date} onChange={e => setData({...data, date: e.target.value})} />
        <input className="w-full bg-black p-3 rounded-xl border border-white/5 text-xs text-white outline-none"
          placeholder="Venue Name" value={data.hallName} onChange={e => setData({...data, hallName: e.target.value})} />
        <input className="w-full bg-black p-3 rounded-xl border border-white/5 text-xs text-white outline-none"
          placeholder="Google Maps URL" value={data.location} onChange={e => setData({...data, location: e.target.value})} />
        <textarea className="w-full bg-black p-3 rounded-xl border border-white/5 text-xs text-white outline-none resize-none min-h-[80px]"
          placeholder="Message" value={data.message} onChange={e => setData({...data, message: e.target.value})} />
        <button onClick={save} disabled={saving}
          className="w-full bg-gold text-black py-3 rounded-xl font-bold uppercase text-xs tracking-widest disabled:opacity-50">
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}

// ─── RSVP COUNT (live) ────────────────────────────────────────────────────────
function RsvpCount({ inviteId }) {
  const [counts, setCounts] = useState({ yes: 0, no: 0 });
  useEffect(() => {
    const q = collection(db, "submissions", inviteId, "rsvps");
    return onSnapshot(q, snap => {
      let yes = 0, no = 0;
      snap.docs.forEach(d => { if (d.data().response === 'yes') yes++; else no++; });
      setCounts({ yes, no });
    });
  }, [inviteId]);
  return { yes: counts.yes, no: counts.no };
}

// Card shown in admin for each submission
function AdminCard({ item, onEdit, onApprove, onDelete, onCopyLink, onCopyPreview }) {
  const rsvp        = RsvpCount({ inviteId: item.id });
  const theme       = THEMES.find(t => t.id === item.themeId) || THEMES[0];
  const hasActivity = (item.viewCount > 0) || (rsvp.yes + rsvp.no > 0);

  return (
    <div className="bg-[#0f0f0f] rounded-2xl border border-white/5 overflow-hidden hover:border-gold/10 transition-colors">

      {/* Top strip: cover banner */}
      <div className="relative h-24 w-full overflow-hidden">
        <img src={item.coverPhoto} className="w-full h-full object-cover opacity-60" alt="" />
        {/* gradient overlay */}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.3) 60%, rgba(0,0,0,0.1) 100%)' }} />

        {/* Left: name + event type */}
        <div className="absolute inset-0 flex items-center px-5 gap-4">
          <div className="flex-1 min-w-0">
            <h4 className="font-vibes text-3xl leading-none truncate" style={{ color: theme.accent }}>{item.name || '—'}</h4>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {item.eventLabel && (
                <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full border font-cinzel"
                  style={{ borderColor: theme.accent + '40', color: theme.accent + 'aa' }}>
                  {item.eventLabel}
                </span>
              )}
              {item.approved && (
                <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 font-cinzel">
                  ✓ Live
                </span>
              )}
              {item.rejected && (
                <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 font-cinzel">
                  Rejected
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="px-5 py-4">

        {/* Meta row */}
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-white/30 uppercase tracking-widest font-cinzel mb-3">
          {item.date && <span>📅 {item.date}</span>}
          {item.hallName && <span>📍 {item.hallName}</span>}
          {item.language && item.language !== 'en' && <span>🌐 {LANGUAGES.find(l => l.id === item.language)?.label}</span>}
        
        </div>

        {/* Stats row */}
        {hasActivity && (
          <div className="flex gap-4 mb-4">
            {item.viewCount > 0 && (
              <div className="flex items-center gap-1.5 bg-blue-500/10 border border-blue-400/10 px-3 py-1.5 rounded-xl">
                <Eye size={11} className="text-blue-400" />
                <span className="text-[11px] text-blue-300 font-cinzel">{item.viewCount}</span>
                <span className="text-[9px] text-blue-400/50 uppercase">views</span>
              </div>
            )}
            {(rsvp.yes + rsvp.no) > 0 && (
              <>
                <div className="flex items-center gap-1.5 bg-emerald-500/10 border border-emerald-400/10 px-3 py-1.5 rounded-xl">
                  <Check size={11} className="text-emerald-400" />
                  <span className="text-[11px] text-emerald-300 font-cinzel">{rsvp.yes}</span>
                  <span className="text-[9px] text-emerald-400/50 uppercase">attending</span>
                </div>
                <div className="flex items-center gap-1.5 bg-red-500/10 border border-red-400/10 px-3 py-1.5 rounded-xl">
                  <X size={11} className="text-red-400" />
                  <span className="text-[11px] text-red-300 font-cinzel">{rsvp.no}</span>
                  <span className="text-[9px] text-red-400/50 uppercase">declined</span>
                </div>
              </>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          {/* Preview link (always available pre-approval) */}
          {!item.approved && (
            <button onClick={() => onCopyPreview(item)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-white/8 text-[10px] text-white/40 hover:text-white hover:border-white/20 transition-all uppercase font-cinzel tracking-wider">
              <Share2 size={10}/> Preview
            </button>
          )}

          {/* Approve / Copy Live Link */}
          {!item.approved && !item.rejected && (
            <button onClick={() => onApprove(item.id)}
              className="flex items-center gap-1.5 px-5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-[10px] uppercase font-cinzel tracking-wider transition-colors">
              <Check size={11}/> Approve
            </button>
          )}
          {item.approved && (
            <button onClick={() => onCopyLink(item.id)}
              className="flex items-center gap-1.5 px-5 py-2 rounded-xl bg-gold hover:bg-yellow-400 text-black text-[10px] uppercase font-cinzel tracking-wider transition-colors">
              <Share2 size={10}/> Copy Link
            </button>
          )}

          {/* Edit */}
          <button onClick={() => onEdit(item)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-white/8 text-[10px] text-white/40 hover:text-gold hover:border-gold/30 transition-all uppercase font-cinzel tracking-wider">
            <Edit2 size={10}/> Edit
          </button>

          {/* Reject (pending only) */}
          {!item.approved && !item.rejected && (
            <button onClick={() => updateDoc(doc(db, "submissions", item.id), { rejected: true })}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-red-500/10 text-[10px] text-red-900 hover:text-red-400 hover:border-red-500/30 transition-all uppercase font-cinzel tracking-wider">
              <X size={10}/> Reject
            </button>
          )}

          {/* Delete */}
          <button onClick={() => onDelete(item.id)}
            className="ml-auto flex items-center gap-1 px-3 py-2 rounded-xl text-[10px] text-red-900 hover:text-red-500 transition-all">
            <Trash2 size={10}/> Delete
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── ADMIN PANEL ──────────────────────────────────────────────────────────────
function AdminPanel() {
  const [user,        setUser]        = useState(null);
  const [activeTab,   setActiveTab]   = useState('pending');
  const [submissions, setSubmissions] = useState([]);
  const [editItem,    setEditItem]    = useState(null);

  useEffect(() => { onAuthStateChanged(auth, u => setUser(u)); }, []);

  useEffect(() => {
    if (user?.email !== ADMIN_EMAIL) return;
    const q = query(collection(db, "submissions"), orderBy("timestamp", "desc"));
    return onSnapshot(q, snap => setSubmissions(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, [user]);

  const handleApprove    = (id)   => updateDoc(doc(db, "submissions", id), { approved: true, rejected: false });
  const handleDelete     = (id)   => { if (window.confirm("Delete this invitation?")) deleteDoc(doc(db, "submissions", id)); };
  const handleCopyLink   = (id)   => { navigator.clipboard.writeText(`${window.location.origin}/wedding/${id}`); alert("✅ Live link copied!"); };
  const handleCopyPreview = (item) => { navigator.clipboard.writeText(`${window.location.origin}/wedding/${item.id}?preview=${item.previewToken}`); alert("🔗 Preview link copied!"); };

  if (!user || user.email !== ADMIN_EMAIL) return (
    <div className="h-screen bg-[#0a0a0a] flex flex-col items-center justify-center gap-6">
      <p className="font-cinzel text-gold/40 text-xs uppercase tracking-widest">Admin Access Required</p>
      <button onClick={() => signInWithPopup(auth, googleProvider)}
        className="bg-gold text-black px-10 py-4 rounded-full font-bold uppercase tracking-widest text-xs hover:bg-yellow-400 transition-colors shadow-2xl">
        Login with Google
      </button>
    </div>
  );

  const pending  = submissions.filter(s => !s.approved && !s.rejected);
  const approved = submissions.filter(s =>  s.approved);
  const rejected = submissions.filter(s =>  s.rejected);
  const listMap  = { pending, history: approved, rejected };
  const current  = listMap[activeTab] || [];

  const totalViews = submissions.reduce((a, s) => a + (s.viewCount || 0), 0);

  return (
    <div className="min-h-screen bg-[#080808] text-white">
      {editItem && <EditModal item={editItem} onClose={() => setEditItem(null)} />}

      {/* Header */}
      <div className="border-b border-white/5 px-6 md:px-12 py-5 flex justify-between items-center sticky top-0 bg-[#080808]/95 backdrop-blur z-40">
        <div>
          <h2 className="font-cinzel text-gold text-lg tracking-widest uppercase">Admin Panel</h2>
          <p className="text-[9px] text-white/20 uppercase tracking-widest mt-0.5">{user.email}</p>
        </div>
        <button onClick={() => signOut(auth)} className="text-[10px] text-red-500/60 hover:text-red-400 font-cinzel uppercase tracking-wider border border-red-500/10 hover:border-red-500/30 px-4 py-2 rounded-full transition-all">
          Logout
        </button>
      </div>

      <div className="max-w-5xl mx-auto px-6 md:px-12 py-8 space-y-8">

        {/* Stats summary row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Total',    value: submissions.length, color: 'text-white' },
            { label: 'Pending',  value: pending.length,     color: 'text-yellow-400' },
            { label: 'Live',     value: approved.length,    color: 'text-emerald-400' },
            { label: 'Views',    value: totalViews,          color: 'text-blue-400' },
          ].map(s => (
            <div key={s.label} className="bg-[#111] rounded-2xl border border-white/5 px-5 py-4">
              <p className="text-[9px] uppercase tracking-widest text-white/30 font-cinzel">{s.label}</p>
              <p className={`text-2xl font-cinzel font-bold mt-1 ${s.color}`}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-white/5 pb-0">
          {[
            { key: 'pending',  label: 'Pending',  count: pending.length },
            { key: 'history',  label: 'Live',     count: approved.length },
            { key: 'rejected', label: 'Rejected', count: rejected.length },
          ].map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={`px-5 py-2.5 text-[10px] uppercase font-cinzel tracking-widest border-b-2 -mb-px transition-all ${
                activeTab === tab.key
                  ? 'border-gold text-gold'
                  : 'border-transparent text-white/30 hover:text-white/60'
              }`}>
              {tab.label}
              {tab.count > 0 && (
                <span className={`ml-2 px-1.5 py-0.5 rounded-full text-[8px] ${activeTab === tab.key ? 'bg-gold text-black' : 'bg-white/10 text-white/50'}`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Cards */}
        <div className="space-y-4">
          {current.map(item => (
            <AdminCard
              key={item.id}
              item={item}
              onEdit={setEditItem}
              onApprove={handleApprove}
              onDelete={handleDelete}
              onCopyLink={handleCopyLink}
              onCopyPreview={handleCopyPreview}
            />
          ))}
          {current.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <p className="text-4xl opacity-20">🎊</p>
              <p className="text-white/15 font-cinzel text-sm uppercase tracking-widest">Nothing here yet</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── APP ROOT ─────────────────────────────────────────────────────────────────
export default function App() {
  const [view, setView] = useState('home');
  return (
<Router>
  <Routes>
    {/* Public Invitation View */}
    <Route path="/wedding/:id" element={<WeddingView />} />

    {/* Main Platform Route */}
    <Route path="/" element={
      <div className="min-h-screen bg-[#0a0a0a] text-white selection:bg-gold/30">
        <style>{`
          .hide-scrollbar::-webkit-scrollbar { display: none; }
          .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
          @import url('https://fonts.googleapis.com/css2?family=Great+Vibes&family=Cinzel:wght@400;700&display=swap');
        `}</style>

        {/* --- PROFESSIONAL BUSINESS NAVIGATION --- */}
        <nav className="fixed top-0 w-full flex justify-between items-center p-6 z-[100] bg-gradient-to-b from-black via-black/80 to-transparent">
          <div 
            className="text-gold font-cinzel text-xl font-bold cursor-pointer tracking-tighter"
            onClick={() => setView('guest')}
          >
            Welcome all
          </div>
          
          <div className="flex gap-4">
            <button 
              onClick={() => setView('admin')} 
              className="text-gold/60 font-cinzel text-[10px] uppercase tracking-[0.2em] hover:text-gold transition-colors"
            >
              Admin Access
            </button>
          </div>
        </nav>

        {/* --- MAIN CONTENT AREA --- */}
        <main className="pt-24 pb-12 px-6 max-w-6xl mx-auto">
          {view === 'admin' ? (
            <div className="relative animate-in fade-in duration-500">
              <AdminPanel />
              <button 
                onClick={() => setView('guest')} 
                className="mt-8 mx-auto block text-gold/40 hover:text-gold text-xs font-cinzel"
              >
                ← Back to Creator
              </button>
            </div>
          ) : (
            <div className="space-y-12 animate-in slide-in-from-bottom-4 duration-700">
              {/* Marketing Header (SEO Optimized) */}
              <header className="text-center space-y-4">
                <h1 className="text-5xl md:text-7xl font-vibes text-gold">Create Your Digital Invitation</h1>
                <p className="font-cinzel text-[10px] uppercase tracking-[0.4em] text-gold/50">Modern • Multilingual • Elegant</p>
              </header>

              {/* The Creation Tool (Immediate Value) */}
              <section className="bg-[#111] rounded-3xl border border-gold/10 shadow-2xl p-4 md:p-8">
                <GuestRegistry onComplete={() => {
                  console.log("Invitation Created!");
                  // You could redirect to a success page here
                }} />
              </section>
            </div>
          )}
        </main>

        {/* --- MINIMAL BUSINESS FOOTER --- */}
        <footer className="py-12 border-t border-gold/5 text-center">
          <p className="font-cinzel text-[12px] uppercase tracking-widest text-gold/30">
            © 2026 Anothershadow • Eco-Friendly Digital Solutions
          </p>
        </footer>
      </div>
    } />
  </Routes>
</Router>
  );
  
}